/**
 * Critical path through a task dependency DAG: the chain of dated tasks whose
 * summed duration (due − start, inclusive) is longest. Returns the set of task
 * ids on that path (empty when there are no dated, linked tasks).
 *
 * Only tasks with both a start and due date participate (others have no bar).
 * Cycles can't occur — the dependency endpoint rejects them — so a Kahn
 * topological order always exists.
 */
type T = { id: string; startDate: string | null; dueDate: string | null };
type E = { blockerId: string; blockedId: string };

const DAY = 86_400_000;

export function computeCriticalPath(tasks: T[], edges: E[]): Set<string> {
  const dur = new Map<string, number>();
  for (const t of tasks) {
    if (t.startDate && t.dueDate) {
      const d = (Date.parse(t.dueDate) - Date.parse(t.startDate)) / DAY + 1;
      dur.set(t.id, Math.max(1, d));
    }
  }
  const nodes = [...dur.keys()];
  if (nodes.length === 0) return new Set();

  const adj = new Map<string, string[]>();
  const indeg = new Map<string, number>();
  for (const id of nodes) indeg.set(id, 0);
  for (const e of edges) {
    if (!dur.has(e.blockerId) || !dur.has(e.blockedId)) continue;
    const arr = adj.get(e.blockerId) ?? [];
    arr.push(e.blockedId);
    adj.set(e.blockerId, arr);
    indeg.set(e.blockedId, (indeg.get(e.blockedId) ?? 0) + 1);
  }

  // Kahn topological order.
  const queue = nodes.filter((n) => (indeg.get(n) ?? 0) === 0);
  const order: string[] = [];
  const indegW = new Map(indeg);
  while (queue.length) {
    const n = queue.shift()!;
    order.push(n);
    for (const m of adj.get(n) ?? []) {
      indegW.set(m, (indegW.get(m) ?? 0) - 1);
      if ((indegW.get(m) ?? 0) === 0) queue.push(m);
    }
  }

  // Longest accumulated duration ending at each node + its predecessor.
  const best = new Map<string, number>();
  const pred = new Map<string, string | null>();
  for (const n of nodes) {
    best.set(n, dur.get(n)!);
    pred.set(n, null);
  }
  for (const n of order) {
    const bn = best.get(n)!;
    for (const m of adj.get(n) ?? []) {
      const cand = bn + dur.get(m)!;
      if (cand > (best.get(m) ?? 0)) {
        best.set(m, cand);
        pred.set(m, n);
      }
    }
  }

  // Trace back from the node with the maximum path length.
  let end: string | null = null;
  let max = -1;
  for (const n of nodes) {
    if ((best.get(n) ?? 0) > max) {
      max = best.get(n)!;
      end = n;
    }
  }
  const path = new Set<string>();
  let cur = end;
  while (cur) {
    path.add(cur);
    cur = pred.get(cur) ?? null;
  }
  // A single isolated task isn't a meaningful "critical path".
  return path.size > 1 ? path : new Set();
}

/** Percent complete (0–100) for a task bar: status-based, refined by subtasks. */
export function taskProgress(t: {
  status: string;
  subtaskCount?: number;
  subtaskCompletedCount?: number;
}): number {
  if (t.status === "done") return 100;
  if (t.subtaskCount && t.subtaskCount > 0)
    return Math.round(((t.subtaskCompletedCount ?? 0) / t.subtaskCount) * 100);
  if (t.status === "doing") return 50;
  return 0;
}
